import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export type VertragState =
  | 'draft'
  | 'planned'
  | 'active'
  | 'expired'
  | 'terminated'
  | 'superseded'
  | 'rejected'

export type VertragCategory =
  | 'school_staff'
  | 'verwaltung'
  | 'fahrer_springer'
  | 'ferien_pool'
  | 'spueler'
  | 'catering'
  | 'auszubildender'
  | 'sonstige'

export interface Vertrag {
  id: number
  // 'dim_pending' = synthesized from dim.PersonEmployment.status='pending'
  // (FM _Zukunft data, no real ops.PersonVertrag row yet). id is negative for these.
  source: 'filemaker' | 'intranet' | 'dim_pending'
  fmId: number | null
  personMainId: number
  personalId: number | null
  vonDatum: string
  bisDatum: string | null
  aktuell: boolean
  state: VertragState
  parentDraftId: number | null
  // Content
  anstellungsart: string | null
  arbeitszeitKategorie: string | null
  arbeitszeitVon: string | null
  arbeitszeitBis: string | null
  arbeitszeitDifferenz: number | null
  wochenstunden: number | null
  arbeitstage: string | null
  berufsbezeichnung: string | null
  firma: string | null
  monatsgehalt: number | null
  monatsgehaltsart: string | null
  stdLohn: number | null
  stdBasiert: boolean | null
  urlaubsanspruch: number | null
  ferienwochen: number | null
  ferienteilnahme: boolean | null
  probezeitMonate: number | null
  arbeitsamt: boolean | null
  essensgeld: boolean | null
  digitalUnterschrift: boolean | null
  schuleId: string | null
  schuleName: string | null
  category: VertragCategory | null
  barcodeUrl: string | null
  barcodeText: string | null
  // Audit
  fmCreatedAt: string | null
  fmCreatedBy: string | null
  fmModifiedAt: string | null
  fmModifiedBy: string | null
  auditCreatedAt: string
  auditCreatedBy: string
  auditModifiedAt: string | null
  auditModifiedBy: string | null
  notes: string | null
  // Derived
  isOpenEnded: boolean
  isLocked: boolean
}

export interface VertragInput {
  personMainId: number
  personalId?: number | null
  vonDatum: string
  bisDatum?: string | null
  parentDraftId?: number | null
  anstellungsart?: string | null
  arbeitszeitKategorie?: string | null
  arbeitszeitVon?: string | null
  arbeitszeitBis?: string | null
  arbeitszeitDifferenz?: number | null
  wochenstunden?: number | null
  arbeitstage?: string | null
  berufsbezeichnung?: string | null
  firma?: string | null
  monatsgehalt?: number | null
  monatsgehaltsart?: string | null
  stdLohn?: number | null
  stdBasiert?: boolean | null
  urlaubsanspruch?: number | null
  ferienwochen?: number | null
  ferienteilnahme?: boolean | null
  probezeitMonate?: number | null
  arbeitsamt?: boolean | null
  essensgeld?: boolean | null
  digitalUnterschrift?: boolean | null
  schuleId?: string | null
  schuleName?: string | null
  category?: VertragCategory | null
  barcodeUrl?: string | null
  barcodeText?: string | null
  notes?: string | null
}

export interface VertragPatch {
  state?: VertragState
  notes?: string | null
  // Content fields (only editable while state='draft')
  vonDatum?: string
  bisDatum?: string | null
  anstellungsart?: string | null
  arbeitszeitKategorie?: string | null
  arbeitszeitVon?: string | null
  arbeitszeitBis?: string | null
  arbeitszeitDifferenz?: number | null
  wochenstunden?: number | null
  arbeitstage?: string | null
  berufsbezeichnung?: string | null
  firma?: string | null
  monatsgehalt?: number | null
  monatsgehaltsart?: string | null
  stdLohn?: number | null
  stdBasiert?: boolean | null
  urlaubsanspruch?: number | null
  ferienwochen?: number | null
  ferienteilnahme?: boolean | null
  probezeitMonate?: number | null
  arbeitsamt?: boolean | null
  essensgeld?: boolean | null
  digitalUnterschrift?: boolean | null
  schuleId?: string | null
  schuleName?: string | null
  category?: VertragCategory | null
}

// ── Enum constants for UI ─────────────────────────────────────────────────────

export const ANSTELLUNGSART_OPTIONS = [
  'Unbefristeter Arbeitsvertrag',
  'Befristeter Arbeitsvertrag',
] as const

export const ARBEITSZEIT_KATEGORIE_OPTIONS = [
  'Vollzeit',
  'Teilzeit',
  'Mini-Job',
  'Azubi',
] as const

export const MONATSGEHALTSART_OPTIONS = [
  'Brutto',
  'Netto',
  'Std.-Lohn',
] as const

export const FIRMA_OPTIONS = [
  'Schulcatering GmbH',
  'GmbH',
] as const

// Category drives DATEV cascade routing (see migration 070 + 124_fix4).
// school_staff → resolved via schuleId; all others → operative-KS per Firma.
// fortbildung removed 2026-05-14 (hard-replaced with verwaltung). ferien_pool
// added (existed in prod data; now first-class).
export const CATEGORY_OPTIONS: ReadonlyArray<{ value: VertragCategory; label: string }> = [
  { value: 'school_staff',    label: 'Schulpersonal' },
  { value: 'verwaltung',      label: 'Verwaltung' },
  { value: 'fahrer_springer', label: 'Fahrer/Springer' },
  { value: 'ferien_pool',     label: 'Ferienpool' },
  { value: 'spueler',         label: 'Spüler' },
  { value: 'catering',        label: 'Catering' },
  { value: 'auszubildender',  label: 'Auszubildende:r' },
  { value: 'sonstige',        label: 'Sonstige' },
] as const

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useVertraege(personMainId: number | null) {
  return useQuery({
    queryKey: ['vertraege', personMainId],
    queryFn: () => api.get<Vertrag[]>(`/api/personen/${personMainId}/vertraege`),
    enabled: !!personMainId,
    staleTime: 60_000,
  })
}

export function useCreateVertrag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: VertragInput) =>
      api.post<{ id: number; state: VertragState; correlation_id: string }>('/api/vertraege', data),
    onSuccess: (_r, vars) =>
      qc.invalidateQueries({ queryKey: ['vertraege', vars.personMainId] }),
  })
}

export function useUpdateVertrag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: VertragPatch }) =>
      api.patch<{ ok: boolean; id: number; state: VertragState; correlation_id: string }>(`/api/vertraege/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vertraege'] }),
  })
}

/** Activate a planned draft — convenience over useUpdateVertrag. */
export function useActivateVertrag() {
  const update = useUpdateVertrag()
  return {
    ...update,
    mutate: (id: number) => update.mutate({ id, patch: { state: 'active' } }),
    mutateAsync: (id: number) => update.mutateAsync({ id, patch: { state: 'active' } }),
  }
}

/** Clone an existing draft as an alternative version (sets parent_draft_id). */
export function useCloneVertrag() {
  const create = useCreateVertrag()
  return {
    ...create,
    mutate: (src: Vertrag, overrides: Partial<VertragInput> = {}) => {
      const input: VertragInput = {
        personMainId: src.personMainId,
        personalId: src.personalId,
        parentDraftId: src.id,
        vonDatum: src.vonDatum,
        bisDatum: src.bisDatum,
        anstellungsart: src.anstellungsart,
        arbeitszeitKategorie: src.arbeitszeitKategorie,
        arbeitszeitVon: src.arbeitszeitVon,
        arbeitszeitBis: src.arbeitszeitBis,
        arbeitszeitDifferenz: src.arbeitszeitDifferenz,
        wochenstunden: src.wochenstunden,
        arbeitstage: src.arbeitstage,
        berufsbezeichnung: src.berufsbezeichnung,
        firma: src.firma,
        monatsgehalt: src.monatsgehalt,
        monatsgehaltsart: src.monatsgehaltsart,
        stdLohn: src.stdLohn,
        stdBasiert: src.stdBasiert,
        urlaubsanspruch: src.urlaubsanspruch,
        ferienwochen: src.ferienwochen,
        ferienteilnahme: src.ferienteilnahme,
        probezeitMonate: src.probezeitMonate,
        arbeitsamt: src.arbeitsamt,
        essensgeld: src.essensgeld,
        digitalUnterschrift: src.digitalUnterschrift,
        schuleId: src.schuleId,
        schuleName: src.schuleName,
        ...overrides,
      }
      return create.mutate(input)
    },
  }
}

/** Reject a draft (explicit discard). */
export function useRejectVertrag() {
  const update = useUpdateVertrag()
  return {
    ...update,
    mutate: (id: number) => update.mutate({ id, patch: { state: 'rejected' } }),
    mutateAsync: (id: number) => update.mutateAsync({ id, patch: { state: 'rejected' } }),
  }
}
